import {
  FormData,
  FormErrors,
  BlacklistVerificationResult,
} from "./types";
import { Reservation } from "../reservations/types/Reservation";
import {
  validarEmail,
  validarTelefono,
  validarCampoRequerido,
  validarCUIT,
  validarDNI,
  esDocumentoExtranjero,
  ERROR_DOCUMENTO,
  ERROR_DOCUMENTO_PERSONA,
} from "../../features/shared/utils/validaciones";
import {
  apellidoObligatorio,
  etiquetaClienteMostrar,
  onlyDigits,
} from "../../features/clients/utils/clientIdentityHelpers";
import { findClienteByDocumentDigits } from "./hooks/useClienteSearch";
import { hayConflicto } from "../reservations/hooks/useReservaValidacion";
import api from "../../config/axios";
import { logger } from "../../utils/logger";
import { formatDateKey, parseDateOnly } from "../shared/utils/dateUtils";

/** Validación sincrónica DNI/CUIT para reserva directa (no Booking/Airbnb). */
export type ValidateDocumentoDirectoResult = {
  digits: string;
  /** Mensaje derivado solo de formato/modo; puede combinarse o reemplazarse tras lookup. */
  syncError: string | undefined;
  /** Si conviene consultar cliente existente por dígitos. */
  shouldLookupCliente: boolean;
};

export function validateDocumentoDirecto(
  value: string,
  nextEsEmpresa: boolean
): ValidateDocumentoDirectoResult {
  const digits = onlyDigits(value || "");

  if (nextEsEmpresa) {
    if (digits.length === 0) {
      return {
        digits,
        syncError: "El CUIT es obligatorio.",
        shouldLookupCliente: false,
      };
    }
    if (digits.length === 7 || digits.length === 8) {
      return {
        digits,
        syncError:
          "Una empresa debe tener CUIT de 11 dígitos. Si es una persona, elegí «Persona».",
        shouldLookupCliente: false,
      };
    }
    if (digits.length !== 11) {
      return {
        digits,
        syncError: "El CUIT debe tener 11 dígitos.",
        shouldLookupCliente: false,
      };
    }
    if (!validarCUIT(value || digits)) {
      return {
        digits,
        syncError: "El CUIT no es válido.",
        shouldLookupCliente: false,
      };
    }
    return { digits, syncError: undefined, shouldLookupCliente: true };
  }

  if (digits.length === 0) {
    return {
      digits,
      syncError: "El DNI es obligatorio.",
      shouldLookupCliente: false,
    };
  }
  // Un documento extranjero no se busca por sus números: pueden ser el DNI de
  // otra persona. El servidor reusa al cliente si el documento es el mismo.
  if (esDocumentoExtranjero(value)) {
    return { digits, syncError: undefined, shouldLookupCliente: false };
  }
  if (digits.length === 11) {
    return {
      digits,
      syncError:
        "Este dato parece un CUIT. Si es una empresa, elegí «Empresa».",
      shouldLookupCliente: true,
    };
  }
  if (digits.length !== 7 && digits.length !== 8) {
    return {
      digits,
      syncError: ERROR_DOCUMENTO_PERSONA,
      shouldLookupCliente: false,
    };
  }
  return { digits, syncError: undefined, shouldLookupCliente: true };
}

export type ValidateFormOptions = {
  esBooking?: boolean;
  esAirbnb?: boolean;
  /** Si no se pasa, se deriva de esBooking || esAirbnb */
  esReservaExterna?: boolean;
  /** Origen real para lista negra / mensajes; si no se pasa se deriva */
  origenReserva?: "booking" | "airbnb" | "directo";
  esEmpresa?: boolean;
  /** Cliente elegido en autocomplete / formulario para no bloquear por DNI duplicado */
  clienteIdSeleccionado?: number | null;
};

export const validateForm = async (
  formData: FormData,
  existingReservations?: Reservation[],
  options?: ValidateFormOptions
): Promise<FormErrors> => {
  const esBooking = options?.esBooking ?? false;
  const esAirbnb = options?.esAirbnb ?? false;
  const esReservaExterna =
    options?.esReservaExterna ?? (esBooking || esAirbnb);
  const origenReserva: "booking" | "airbnb" | "directo" =
    options?.origenReserva ??
    (esBooking ? "booking" : esAirbnb ? "airbnb" : "directo");
  const esEmpresa = options?.esEmpresa ?? false;
  const clienteIdSeleccionado =
    options?.clienteIdSeleccionado != null &&
    Number(options.clienteIdSeleccionado) > 0
      ? Number(options.clienteIdSeleccionado)
      : null;
  const newErrors: FormErrors = {};

  // Validaciones básicas
  if (!formData.entrada) {
    newErrors.entrada = "La fecha de entrada es obligatoria";
  }
  if (!formData.salida) {
    newErrors.salida = "La fecha de salida es obligatoria";
  }

  // Validar rango de fechas si ambas están presentes
  if (formData.entrada && formData.salida) {
    const entrada =
      formData.entrada instanceof Date
        ? formData.entrada
        : parseDateOnly(formData.entrada);
    const salida =
      formData.salida instanceof Date
        ? formData.salida
        : parseDateOnly(formData.salida);

    if (salida <= entrada) {
      newErrors.salida =
        "La fecha de salida debe ser posterior a la fecha de entrada";
    } else {
      // Validar mínimo 1 noche
      const daysDifference = Math.ceil(
        (salida.getTime() - entrada.getTime()) / (1000 * 3600 * 24)
      );
      if (daysDifference < 1) {
        newErrors.salida =
          "La reserva debe ser de al menos 1 noche. Las fechas de entrada y salida no pueden ser iguales";
      }
    }
  }

  if (!formData.departamento) {
    newErrors.departamento = "El departamento es obligatorio";
  }
  if (!validarCampoRequerido(formData.nombre || "")) {
    newErrors.nombre = esEmpresa ? "La razón social es obligatoria" : "El nombre es obligatorio";
  }
  if (apellidoObligatorio(esEmpresa) && !validarCampoRequerido(formData.apellido || "")) {
    newErrors.apellido = "El apellido es obligatorio";
  }
  let docDigits = "";

  if (esReservaExterna) {
    if (!validarCampoRequerido((formData.dni || "").trim())) {
      newErrors.dni = esBooking
        ? "El código de reserva Booking es obligatorio"
        : "El código de reserva Airbnb es obligatorio";
    }
  } else {
    const docRes = validateDocumentoDirecto(formData.dni || "", esEmpresa);
    docDigits = docRes.digits;

    if (clienteIdSeleccionado) {
      if (!docRes.shouldLookupCliente && docRes.syncError) {
        newErrors.dni = docRes.syncError;
      }
    } else if (!docRes.shouldLookupCliente) {
      if (docRes.syncError) {
        newErrors.dni = docRes.syncError;
      }
    } else {
      try {
        const existente = await findClienteByDocumentDigits(formData.dni || "");
        const mismoCliente =
          existente &&
          clienteIdSeleccionado != null &&
          existente.id === clienteIdSeleccionado;

        if (mismoCliente) {
          /* ok */
        } else if (esEmpresa) {
          if (existente?.es_empresa) {
            // CUIT repetido en empresa es una advertencia visual, no un bloqueo:
            // varias sucursales pueden compartir CUIT.
          } else if (existente) {
            const nombreLista = etiquetaClienteMostrar(existente);
            newErrors.dni = `Este número coincide con una persona cargada como ${nombreLista}. Revisá si corresponde elegir «Persona».`;
          }
        } else {
          const len = docDigits.length;
          if (len === 11) {
            if (existente?.es_empresa) {
              const nombreLista = etiquetaClienteMostrar(existente);
              newErrors.dni = `Este número coincide con una empresa cargada como ${nombreLista}. Elegí «Empresa» o seleccioná ese cliente.`;
            } else if (docRes.syncError) {
              newErrors.dni = docRes.syncError;
            }
          } else if (len === 7 || len === 8) {
            if (existente?.es_empresa) {
              newErrors.dni =
                "Este número coincide con una empresa, revisá el dato.";
            } else if (existente) {
              const nombreLista = etiquetaClienteMostrar(existente);
              newErrors.dni = `Este DNI ya pertenece a ${nombreLista}. Seleccioná ese cliente desde Buscar cliente.`;
            }
          }
        }
      } catch {
        if (docRes.syncError) {
          newErrors.dni = docRes.syncError;
        }
      }

      if (
        !newErrors.dni &&
        !esEmpresa &&
        docDigits.length === 11 &&
        docRes.syncError
      ) {
        newErrors.dni = docRes.syncError;
      }
    }
  }

  if (!esReservaExterna && !validarCampoRequerido(formData.telefono1 || "")) {
    newErrors.telefono1 = "El teléfono principal es obligatorio";
  }
  if (formData.email1 && !validarEmail(formData.email1)) {
    newErrors.email1 = "El email no tiene un formato válido";
  }
  if (
    !esReservaExterna &&
    formData.telefono1 &&
    !validarTelefono(formData.telefono1)
  ) {
    newErrors.telefono1 = "El teléfono no tiene un formato válido";
  }

  // Verificar superposiciones solo si tenemos las fechas y el departamento
  if (formData.entrada && formData.salida && formData.departamento) {
    const reservaTemporal: Reservation = {
      id: 0,
      nombre: formData.nombre || "",
      departamento: formData.departamento,
      entrada:
        formData.entrada instanceof Date
          ? formatDateKey(formData.entrada)
          : String(formData.entrada),
      salida:
        formData.salida instanceof Date
          ? formatDateKey(formData.salida)
          : String(formData.salida),
      estado: "confirmada",
      pago: 0,
    };
    const hasOverlap = hayConflicto(
      reservaTemporal,
      existingReservations || [],
      undefined
    );
    if (hasOverlap) {
      newErrors.entrada = "Hay superposición con otra reserva";
      newErrors.salida = "Hay superposición con otra reserva";
    }
  }

  if (formData.nombre && formData.apellido && !esReservaExterna) {
    try {
      const blacklistResponse = await api.post("/lista-negra/verificar", {
        nombre: formData.nombre,
        apellido: formData.apellido,
        dni: formData.dni || undefined,
        telefono: formData.telefono1 || undefined,
        email: formData.email1 || undefined,
        origen_reserva: origenReserva,
      });

      const payload = blacklistResponse.data as
        | { success?: boolean; data?: BlacklistVerificationResult }
        | BlacklistVerificationResult;

      const blacklistResult: BlacklistVerificationResult | undefined =
        payload &&
        typeof payload === "object" &&
        "data" in payload &&
        payload.data &&
        typeof payload.data === "object" &&
        "en_lista_negra" in payload.data
          ? payload.data
          : payload && typeof payload === "object" && "en_lista_negra" in payload
            ? (payload as BlacklistVerificationResult)
            : undefined;

      if (
        blacklistResult &&
        (blacklistResult.decision === "error" ||
          (blacklistResult.en_lista_negra &&
            blacklistResult.decision !== "clear"))
      ) {
        newErrors.blacklist_warning = blacklistResult;
      }
    } catch (error) {
      logger.error("Error verificando lista negra:", error);
      newErrors.blacklist_warning = {
        en_lista_negra: false,
        score: 0,
        decision: "error",
        confidence: "alta",
        razones: ["No se pudo verificar lista negra"],
        evidencias: [],
        faltantes: [],
        coincidencia_fuerte: false,
        requiere_revision_manual: true,
        verificacion_error: true,
        error:
          "No se pudo verificar lista negra. Probá guardar nuevamente antes de continuar.",
      };
    }
  }

  return newErrors;
};

export const validateEstacionamientoForm = async (
  formData: FormData
): Promise<FormErrors> => {
  const errors: FormErrors = {};

  if (!formData.estacionamiento) {
    errors.estacionamiento = "Debe seleccionar un estacionamiento.";
  }

  // DNI es opcional, pero si se ingresa, se valida el formato
  if (formData.dni && formData.dni.trim() && !validarDNI(formData.dni)) {
    errors.dni = ERROR_DOCUMENTO;
  }

  // Validar fechas
  if (!formData.entrada || !formData.salida) {
    errors.entrada = "Las fechas de entrada y salida son obligatorias.";
  } else if (formData.salida <= formData.entrada) {
    errors.salida = "La fecha de salida debe ser posterior a la de entrada.";
  }

  return errors;
};
